const STORAGE_KEY = "outreachos_attribution_v1";
const EXPIRATION_MS = 90 * 24 * 60 * 60 * 1000;
const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "referral_code",
  "referral_rsvp_id",
];

function trimToNull(value) {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

function getStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage || null;
  } catch {
    return null;
  }
}

function buildPagePath(locationLike) {
  const pathname = trimToNull(locationLike?.pathname) || "";
  const search = trimToNull(locationLike?.search) || "";
  const hash = trimToNull(locationLike?.hash) || "";
  const pagePath = `${pathname}${search}${hash}`;
  return pagePath || null;
}

function buildLandingPage(locationLike) {
  const href = trimToNull(locationLike?.href);
  if (href) {
    try {
      const parsed = new URL(href);
      return `${parsed.pathname}${parsed.search}${parsed.hash}` || href;
    } catch {
      return href;
    }
  }

  return buildPagePath(locationLike);
}

function parseUrlAttribution(locationLike, documentLike, timestamp) {
  const search = trimToNull(locationLike?.search) || "";
  const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
  const referralCode = trimToNull(params.get("referral_code")) || trimToNull(params.get("rid"));

  const touch = {
    utm_source: trimToNull(params.get("utm_source")),
    utm_medium: trimToNull(params.get("utm_medium")),
    utm_campaign: trimToNull(params.get("utm_campaign")),
    utm_term: trimToNull(params.get("utm_term")),
    utm_content: trimToNull(params.get("utm_content")),
    referral_code: referralCode,
    referral_rsvp_id: trimToNull(params.get("referral_rsvp_id")),
    landing_page: buildLandingPage(locationLike),
    referrer: trimToNull(documentLike?.referrer),
    timestamp,
  };

  return ATTRIBUTION_KEYS.some((key) => touch[key]) ? touch : null;
}

function readStoredRecord() {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    try {
      storage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage cleanup failures
    }
    return null;
  }
}

function writeStoredRecord(record) {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore storage write failures
  }
}

function clearStoredRecord() {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(STORAGE_KEY);
  } catch {
    // ignore storage cleanup failures
  }
}

function getTimestampMs(value) {
  const parsed = Date.parse(String(value || ""));
  return Number.isFinite(parsed) ? parsed : NaN;
}

function buildPayloadTouch(touch) {
  if (!touch || typeof touch !== "object") return null;

  const payloadTouch = {
    landing_page: trimToNull(touch.landing_page),
    referrer: trimToNull(touch.referrer),
    attributed_at: trimToNull(touch.attributed_at ?? touch.timestamp),
    utm_source: trimToNull(touch.utm_source),
    utm_medium: trimToNull(touch.utm_medium),
    utm_campaign: trimToNull(touch.utm_campaign),
    utm_term: trimToNull(touch.utm_term),
    utm_content: trimToNull(touch.utm_content),
    referral_code: trimToNull(touch.referral_code),
    referral_rsvp_id: trimToNull(touch.referral_rsvp_id),
  };

  return Object.values(payloadTouch).some(Boolean) ? payloadTouch : null;
}

function isExpiredRecord(record, nowMs = Date.now()) {
  const expiresAtMs = getTimestampMs(record?.expires_at);
  if (Number.isFinite(expiresAtMs)) {
    return nowMs >= expiresAtMs;
  }

  const latestTouchMs = Math.max(
    getTimestampMs(record?.first_touch?.timestamp),
    getTimestampMs(record?.last_touch?.timestamp)
  );

  if (!Number.isFinite(latestTouchMs)) return true;
  return nowMs >= latestTouchMs + EXPIRATION_MS;
}

export function clearExpiredAttribution() {
  const record = readStoredRecord();
  if (!record) return null;

  if (isExpiredRecord(record)) {
    clearStoredRecord();
    return null;
  }

  return record;
}

export function getPersistedAttribution() {
  return clearExpiredAttribution();
}

export function captureAttributionFromLocation(locationLike, documentLike) {
  const existing = clearExpiredAttribution();
  const timestamp = new Date(Date.now()).toISOString();
  const nextTouch = parseUrlAttribution(locationLike, documentLike, timestamp);

  if (!nextTouch) {
    return existing;
  }

  const record = {
    version: 1,
    expires_at: new Date(Date.now() + EXPIRATION_MS).toISOString(),
    first_touch: existing?.first_touch || nextTouch,
    last_touch: nextTouch,
  };

  writeStoredRecord(record);
  return record;
}

export function mergeAttributionIntoPayload(payload, currentLocationLike, documentLike) {
  const basePayload = payload && typeof payload === "object" ? { ...payload } : {};
  const record =
    captureAttributionFromLocation(currentLocationLike, documentLike) ||
    getPersistedAttribution();
  const currentTouch = parseUrlAttribution(
    currentLocationLike,
    documentLike,
    new Date(Date.now()).toISOString()
  );
  const effectiveTouch = currentTouch || record?.last_touch || record?.first_touch || null;
  const firstTouchPayload = buildPayloadTouch(record?.first_touch);
  const lastTouchPayload = buildPayloadTouch(record?.last_touch);
  const pagePath = buildPagePath(currentLocationLike);
  const currentReferrer = trimToNull(documentLike?.referrer);

  if (!trimToNull(basePayload.page_path) && pagePath) {
    basePayload.page_path = pagePath;
  }

  if (!trimToNull(basePayload.referrer) && currentReferrer) {
    basePayload.referrer = currentReferrer;
  } else if (!trimToNull(basePayload.referrer) && effectiveTouch?.referrer) {
    basePayload.referrer = effectiveTouch.referrer;
  }

  for (const key of ATTRIBUTION_KEYS) {
    if (!trimToNull(basePayload[key]) && effectiveTouch?.[key]) {
      basePayload[key] = effectiveTouch[key];
    }
  }

  if (!trimToNull(basePayload.landing_page) && effectiveTouch?.landing_page) {
    basePayload.landing_page = effectiveTouch.landing_page;
  }

  const effectiveAttributedAt = trimToNull(effectiveTouch?.attributed_at ?? effectiveTouch?.timestamp);
  if (!trimToNull(basePayload.attribution_timestamp) && effectiveAttributedAt) {
    basePayload.attribution_timestamp = effectiveAttributedAt;
  }

  if (firstTouchPayload && basePayload.first_touch == null) {
    basePayload.first_touch = firstTouchPayload;
  }

  if (lastTouchPayload && basePayload.last_touch == null) {
    basePayload.last_touch = lastTouchPayload;
  }

  return basePayload;
}
